import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import BottomNav from "../components/BottomNav";
import { 
  MapPin, Store, Users, Loader2, ArrowLeft, Star, Navigation, ChevronDown, ChevronRight, List, X
} from "lucide-react";
import { toast } from "sonner";

const VenuesPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [radius, setRadius] = useState(20);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [followingVenues, setFollowingVenues] = useState([]);
  const [followLoading, setFollowLoading] = useState({});
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [showRadiusDropdown, setShowRadiusDropdown] = useState(false);
  
  // All Venues state
  const [showAllVenues, setShowAllVenues] = useState(false);
  const [allVenues, setAllVenues] = useState([]);
  const [allVenuesLoading, setAllVenuesLoading] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [venueLocations, setVenueLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);

  const radiusOptions = [5, 20, 50, 100];

  useEffect(() => {
    fetchFollowingVenues();
  }, []);

  const fetchFollowingVenues = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/user/following-venues`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFollowingVenues(response.data);
    } catch (e) {
      console.error("Error fetching following venues:", e);
    }
  };

  const getUserLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation not supported"));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          reject(error);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  };

  const handleNearbySearch = async () => {
    setLocationLoading(true);
    setLoading(true);
    setHasSearched(true);
    setShowAllVenues(false);
    
    try {
      let location = userLocation;
      
      if (!location) {
        try {
          location = await getUserLocation();
          setUserLocation(location);
        } catch (e) {
          toast.error("Unable to get your location. Please enable location services.");
          setLoading(false);
          setLocationLoading(false);
          return;
        }
      }
      
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(
        `${API}/venues/search/nearby?lat=${location.lat}&lng=${location.lng}&radius=${radius}`,
        { headers }
      );
      setVenues(response.data);
      if (response.data.length === 0) {
        toast.info(`No venues found within ${radius} miles`);
      } else {
        toast.success(`Found ${response.data.length} venue${response.data.length > 1 ? 's' : ''} within ${radius} miles`);
      }
    } catch (e) {
      toast.error("Failed to search venues");
    } finally {
      setLoading(false);
      setLocationLoading(false);
    }
  };

  const handleShowAllVenues = async () => {
    setShowAllVenues(true);
    setHasSearched(false);
    setSelectedVenue(null);
    setAllVenuesLoading(true);
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/venues/all`, { headers });
      setAllVenues(response.data);
    } catch (e) {
      toast.error("Failed to load venues");
    } finally {
      setAllVenuesLoading(false);
    }
  };

  const handleSelectVenue = async (venue) => {
    setSelectedVenue(venue);
    setLocationsLoading(true);
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/venues/${venue.id}/locations`, { headers });
      setVenueLocations(response.data.locations || []);
    } catch (e) {
      toast.error("Failed to load locations");
    } finally {
      setLocationsLoading(false);
    }
  };

  const handleBackToVenues = () => {
    setSelectedVenue(null);
    setVenueLocations([]);
  };

  const handleCloseAllVenues = () => {
    setShowAllVenues(false);
    setSelectedVenue(null);
    setVenueLocations([]);
  };

  const handleFollow = async (locationId) => {
    if (!user) {
      navigate(`/auth?redirect=/venues`);
      return;
    }

    setFollowLoading(prev => ({ ...prev, [locationId]: true }));
    try {
      await axios.post(`${API}/venues/${locationId}/follow`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Now following this venue!");
      setVenues(prev => prev.map(v => 
        v.id === locationId ? { ...v, is_following: true } : v
      ));
      setVenueLocations(prev => prev.map(v => 
        v.id === locationId ? { ...v, is_following: true } : v
      ));
      fetchFollowingVenues();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to follow");
    } finally {
      setFollowLoading(prev => ({ ...prev, [locationId]: false }));
    }
  };

  const handleUnfollow = async (locationId) => {
    setFollowLoading(prev => ({ ...prev, [locationId]: true }));
    try {
      await axios.delete(`${API}/venues/${locationId}/follow`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Unfollowed venue");
      setVenues(prev => prev.map(v => 
        v.id === locationId ? { ...v, is_following: false } : v
      ));
      setVenueLocations(prev => prev.map(v => 
        v.id === locationId ? { ...v, is_following: false } : v
      ));
      setFollowingVenues(prev => prev.filter(v => v.id !== locationId));
    } catch (e) {
      toast.error("Failed to unfollow");
    } finally {
      setFollowLoading(prev => ({ ...prev, [locationId]: false }));
    }
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    return `https://res.cloudinary.com/drwfgcqpn/image/upload/${path}`;
  };

  // Render location card (reusable for both search results and venue locations)
  const renderLocationCard = (location, showVenueName = true) => (
    <div
      key={location.id}
      className="glass-card p-4"
      data-testid={`venue-result-${location.id}`}
    >
      <button
        onClick={() => navigate(`/venue/${location.id}`)}
        className="w-full text-left"
      >
        <div className="flex items-start gap-3">
          <Avatar className="w-14 h-14">
            <AvatarImage src={getImageUrl(location.venue_logo)} />
            <AvatarFallback className="bg-primary/20 text-primary">
              <Store className="w-6 h-6" />
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                {showVenueName && <h3 className="text-white font-medium">{location.venue_name}</h3>}
                <p className={showVenueName ? "text-white/60 text-sm" : "text-white font-medium"}>{location.name}</p>
              </div>
              {location.distance_miles !== undefined && (
                <span className="text-primary text-sm font-medium whitespace-nowrap">
                  {location.distance_miles} mi
                </span>
              )}
            </div>
            {location.address && (
              <p className="text-white/40 text-xs mt-1 flex items-center gap-1">
                <MapPin className="w-3 h-3 shrink-0" />
                <span className="truncate">{location.address}</span>
              </p>
            )}
            <div className="flex items-center gap-3 mt-2">
              <span className="text-white/40 text-xs flex items-center gap-1">
                <Users className="w-3 h-3" />
                {location.followers?.length || 0} followers
              </span>
              {location.stars?.length > 0 && (
                <span className="text-white/40 text-xs flex items-center gap-1">
                  <Star className="w-3 h-3" />
                  {location.stars.length} stars
                </span>
              )}
            </div>
          </div>
        </div>
      </button>

      {/* Follow/Unfollow Button */}
      <div className="mt-3 pt-3 border-t border-white/10">
        {location.is_following ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleUnfollow(location.id)}
            disabled={followLoading[location.id]}
            className="w-full border-white/20 text-white/60"
            data-testid={`unfollow-venue-${location.id}`}
          >
            {followLoading[location.id] ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Following"
            )}
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={() => handleFollow(location.id)}
            disabled={followLoading[location.id]}
            className="btn-primary w-full"
            data-testid={`follow-venue-${location.id}`}
          >
            {followLoading[location.id] ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Users className="w-4 h-4 mr-2" />
                Follow
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="venues-page">
      {/* Header */}
      <div className="sticky top-0 z-10 glass-card border-b border-white/10 px-4 py-3">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => {
              if (selectedVenue) {
                handleBackToVenues();
              } else if (showAllVenues) {
                handleCloseAllVenues();
              } else {
                navigate(-1);
              }
            }} 
            className="text-white/60 hover:text-white" 
            data-testid="back-btn"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-white">
              {selectedVenue ? selectedVenue.name : showAllVenues ? "All Venues" : "Find Venues"}
            </h1>
            <p className="text-white/50 text-xs">
              {selectedVenue 
                ? `${venueLocations.length} location${venueLocations.length !== 1 ? 's' : ''}`
                : showAllVenues 
                  ? "Browse participating venues"
                  : "Discover bars and restaurants near you"
              }
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* All Venues - Venue Locations View */}
        {showAllVenues && selectedVenue && (
          <div className="space-y-3">
            {/* Venue Header */}
            <div className="glass-card p-4 flex items-center gap-3">
              <Avatar className="w-16 h-16">
                <AvatarImage src={getImageUrl(selectedVenue.logo)} />
                <AvatarFallback className="bg-primary/20 text-primary">
                  <Store className="w-8 h-8" />
                </AvatarFallback>
              </Avatar>
              <div>
                <h2 className="text-white font-semibold text-lg">{selectedVenue.name}</h2>
                {selectedVenue.description && (
                  <p className="text-white/60 text-sm line-clamp-2">{selectedVenue.description}</p>
                )}
              </div>
            </div>

            {/* Locations List */}
            {locationsLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : venueLocations.length > 0 ? (
              <div className="space-y-3">
                <h3 className="text-white/60 text-sm font-medium">Locations</h3>
                {venueLocations.map((location) => renderLocationCard(location, false))}
              </div>
            ) : (
              <div className="text-center py-8">
                <MapPin className="w-12 h-12 text-white/20 mx-auto mb-3" />
                <p className="text-white/40">No locations found</p>
              </div>
            )}
          </div>
        )}

        {/* All Venues - Master List View */}
        {showAllVenues && !selectedVenue && (
          <div className="space-y-3">
            {allVenuesLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : allVenues.length > 0 ? (
              allVenues.map((venue) => (
                <button
                  key={venue.id}
                  onClick={() => handleSelectVenue(venue)}
                  className="w-full glass-card p-4 text-left hover:bg-white/10 transition-colors flex items-center gap-3"
                  data-testid={`all-venue-${venue.id}`}
                >
                  <Avatar className="w-14 h-14">
                    <AvatarImage src={getImageUrl(venue.logo)} />
                    <AvatarFallback className="bg-primary/20 text-primary">
                      <Store className="w-6 h-6" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium">{venue.name}</h3>
                    {venue.description && (
                      <p className="text-white/60 text-sm truncate">{venue.description}</p>
                    )}
                    <p className="text-white/40 text-xs mt-1 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {venue.location_count} location{venue.location_count !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-white/40" />
                </button>
              ))
            ) : (
              <div className="text-center py-8">
                <Store className="w-12 h-12 text-white/20 mx-auto mb-3" />
                <p className="text-white/40">No venues available</p>
              </div>
            )}
          </div>
        )}

        {/* Main View - Search Options */}
        {!showAllVenues && (
          <>
            {/* Near Me Search */}
            <div className="space-y-3">
              <div className="flex gap-2">
                {/* Radius Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowRadiusDropdown(!showRadiusDropdown)}
                    className="flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
                    data-testid="radius-dropdown"
                  >
                    {radius} miles
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  {showRadiusDropdown && (
                    <div className="absolute top-full mt-1 left-0 bg-background border border-white/20 rounded-lg overflow-hidden z-20">
                      {radiusOptions.map(r => (
                        <button
                          key={r}
                          onClick={() => {
                            setRadius(r);
                            setShowRadiusDropdown(false);
                          }}
                          className={`block w-full px-4 py-2 text-left hover:bg-white/10 ${
                            radius === r ? "text-primary" : "text-white"
                          }`}
                          data-testid={`radius-option-${r}`}
                        >
                          {r} miles
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Search Button */}
                <Button 
                  onClick={handleNearbySearch}
                  disabled={loading}
                  className="btn-primary flex-1"
                  data-testid="search-nearby-btn"
                >
                  {locationLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Navigation className="w-4 h-4 mr-2" />
                  )}
                  {locationLoading ? "Getting Location..." : "Find Venues Near Me"}
                </Button>
              </div>
              <p className="text-white/40 text-xs text-center">
                Uses your device location to find nearby venues
              </p>
            </div>

            {/* All Venues Button */}
            <Button
              onClick={handleShowAllVenues}
              variant="outline"
              className="w-full border-white/20 text-white hover:bg-white/10"
              data-testid="all-venues-btn"
            >
              <List className="w-4 h-4 mr-2" />
              All Venues
            </Button>

            {/* Following Section */}
            {followingVenues.length > 0 && !hasSearched && (
              <div className="space-y-3">
                <h2 className="text-white font-medium flex items-center gap-2">
                  <Store className="w-4 h-4 text-primary" />
                  Venues You Follow
                </h2>
                {followingVenues.map((venue) => (
                  <button
                    key={venue.id}
                    onClick={() => navigate(`/venue/${venue.id}`)}
                    className="w-full glass-card p-4 text-left hover:bg-white/10 transition-colors"
                    data-testid={`following-venue-${venue.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="w-12 h-12">
                        <AvatarImage src={getImageUrl(venue.venue_logo)} />
                        <AvatarFallback className="bg-primary/20 text-primary">
                          <Store className="w-6 h-6" />
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-medium truncate">{venue.venue_name}</h3>
                        <p className="text-white/60 text-sm truncate">{venue.name}</p>
                        {venue.address && (
                          <p className="text-white/40 text-xs truncate flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {venue.address}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Search Results */}
            {hasSearched && (
              <div className="space-y-3">
                <h2 className="text-white font-medium">
                  {loading ? "Searching..." : venues.length > 0 ? `${venues.length} Venue${venues.length > 1 ? 's' : ''} Found` : "No Results"}
                </h2>

                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : venues.length > 0 ? (
                  venues.map((venue) => renderLocationCard(venue))
                ) : (
                  <div className="text-center py-8">
                    <Store className="w-12 h-12 text-white/20 mx-auto mb-3" />
                    <p className="text-white/40">No venues found in this area</p>
                    <p className="text-white/30 text-sm mt-1">Try a larger search radius</p>
                  </div>
                )}
              </div>
            )}

            {/* Initial State */}
            {!hasSearched && followingVenues.length === 0 && (
              <div className="text-center py-8">
                <Store className="w-12 h-12 text-white/20 mx-auto mb-3" />
                <p className="text-white/40">Search for venues near you</p>
                <p className="text-white/30 text-sm mt-1">Tap "Find Venues Near Me" or browse "All Venues"</p>
              </div>
            )}
          </>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default VenuesPage;
